import React from 'react';
import { ChevronDown, Check, ChartLine, Coins, Plus } from 'lucide-react';

import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from 'components/ui/dropdown-menu';

/**
 * El desplegable de la parte de arriba del Sidebar, en UNA sola pieza con las variantes que tiene:
 * el de PLANES (`SidebarPlanSelector`) y el de EMPRESAS
 * (`SidebarCompanySelector`, para quien administra más de una SL). Los dos envoltorios convierten sus
 * datos en `options`; aquí vive todo lo que se ve y se comporta igual:
 *
 * - Lo que dice el botón: la opción elegida; si no hay ninguna y se está en el resumen, el nombre del
 *   resumen («Resumen de planes»); y si no, el texto de «elegir».
 * - Arriba del todo del desplegable, antes de los planes y tras un separador, una acción que lleva al
 *   RESUMEN DE PLANES (la pantalla de aterrizaje multicuenta). No es una opción más: es un sitio al que
 *   ir, con un icono de gráfica de líneas sin marco y un check cuando es donde se está. Se llama igual
 *   que lo que dice el botón cuando se está en ella. El botón secundario «Añadir plan» va al final.
 * - Con nada que elegir (`selectable={false}`: una sola empresa, o un solo plan sin «Añadir plan») no
 *   es un botón: se enseña lo que hay como texto, sin chevron y sin desplegable, para no prometer una
 *   elección que no existe.
 * - Una opción puede llevar una etiqueta pequeña al lado del nombre (`badge`): «Admin» en las empresas.
 * - Colapsado, solo el logo (o un icono si no hay nada elegido) y el chevron.
 *
 * Chrome compartido con Admin (gris/blanco + dark:): mismo criterio que el resto del sidebar.
 */
export interface SidebarSelectorOption {
	id: string;
	label: string;
	subtitle?: string;
	/** El logo, a la medida que pida cada sitio (`h-9 w-9` en la lista, `h-7 w-7` en el botón). */
	logo: (className: string) => React.ReactNode;
	/** Lo que dice el botón cuando es la opción elegida, si no es `label` (planes de empleo: «Plan · Empresa»). */
	triggerLabel?: string;
	/** Una etiqueta pequeña al lado del nombre («Admin» en las empresas), en el botón y en la lista. */
	badge?: string;
	/** En lugar del subtítulo, una etiqueta de aviso («En proceso» en un plan que se está dando de alta). */
	estado?: string;
}

export interface SidebarSelectorSummary {
	label: string;
	/** Es donde se está: el botón dice `label` y la fila lleva el check. */
	active: boolean;
	onSelect: () => void;
}

export interface SidebarSelectorAddAction {
	label: string;
	onSelect: () => void;
}

export interface SidebarSelectorProps {
	options: SidebarSelectorOption[];
	activeId?: string;
	onSelect: (id: string) => void;
	/** Lo que dice el botón sin nada elegido ni resumen activo («Seleccionar plan»). */
	placeholder: string;
	/** Nombre accesible del desplegable. */
	ariaLabel: string;
	isMenuOpen: boolean;
	/** La acción de ir al resumen de planes; sin ella, no hay fila. */
	summary?: SidebarSelectorSummary;
	/** El botón secundario del final («Añadir plan»), debajo del resumen; sin él, no hay botón. */
	addAction?: SidebarSelectorAddAction;
	/** `false`: nada que elegir, se enseña como texto. */
	selectable?: boolean;
}

const ESTILO_BOTON =
	'w-full flex items-center gap-2.5 h-auto px-3 py-2.5 border border-gray-200 bg-white text-sm font-medium text-gray-700 dark:border-sidebar-border dark:bg-sidebar dark:text-foreground';

export const SidebarSelector = ({
	options,
	activeId,
	onSelect,
	placeholder,
	ariaLabel,
	isMenuOpen,
	summary,
	addAction,
	selectable = true
}: SidebarSelectorProps) => {
	const activa = options.find((option) => option.id === activeId);
	const enResumen = !activa && summary?.active;
	const etiqueta = activa
		? (activa.triggerLabel ?? activa.label)
		: enResumen
			? summary.label
			: placeholder;

	const contenidoDelBoton = isMenuOpen ? (
		<>
			{activa && activa.logo('h-7 w-7')}
			{enResumen && (
				<ChartLine
					aria-hidden="true"
					className="h-5 w-5 flex-none text-gray-500 dark:text-muted-foreground"
				/>
			)}
			<span className="flex-1 truncate text-left">{etiqueta}</span>
			{activa?.badge && (
				<Badge variant="outline" className="flex-none font-normal">
					{activa.badge}
				</Badge>
			)}
			{selectable && <ChevronDown className="h-5 w-5 flex-none" />}
		</>
	) : (
		<>
			{activa ? (
				activa.logo('h-7 w-7')
			) : enResumen ? (
				<ChartLine
					aria-hidden="true"
					className="h-5 w-5 text-gray-500 dark:text-muted-foreground"
				/>
			) : (
				<Coins
					data-testid="plan-selector-fallback-icon"
					className="h-5 w-5 text-gray-500 dark:text-muted-foreground"
					aria-hidden="true"
				/>
			)}
			{selectable && <ChevronDown className="h-5 w-5" />}
		</>
	);

	// Sin nada que elegir no es un botón.
	if (!selectable) {
		return (
			<div className={`px-3 pt-4 pb-4 ${!isMenuOpen ? 'px-2' : ''}`}>
				<div
					className={`${ESTILO_BOTON} rounded-md ${!isMenuOpen ? 'justify-center px-2' : ''}`}
					title={etiqueta}
				>
					{contenidoDelBoton}
				</div>
			</div>
		);
	}

	return (
		<div className={`px-3 pt-4 pb-4 ${!isMenuOpen ? 'px-2' : ''}`}>
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					{/* dark:border-sidebar-border, no dark:border-border: este trigger vive sobre el fondo del
					    sidebar (--sidebar-background), no sobre --card; reusar --border (calibrado para leerse
					    sobre --card) se veía como una línea demasiado blanca aquí. El DropdownMenuContent de
					    abajo SÍ vive sobre --card, así que ese mantiene --border. */}
					<Button
						variant="outline"
						// Plegado solo quedan iconos: el nombre es lo que diría el texto (axe, button-name).
						aria-label={isMenuOpen ? undefined : etiqueta}
						className={`${ESTILO_BOTON} transition-colors hover:bg-gray-50 hover:text-gray-700 dark:hover:bg-card ${
							!isMenuOpen ? 'justify-center px-2' : ''
						}`}
					>
						{contenidoDelBoton}
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent
					className="w-64 bg-white border border-gray-200 dark:bg-card dark:border-border"
					align="center"
					side={isMenuOpen ? 'bottom' : 'right'}
					sideOffset={4}
					aria-label={ariaLabel}
				>
					{summary && (
						<>
							{/* Una acción, no una opción más, y la primera: lleva a la vista de conjunto, y los planes
							    vienen después de un separador. Sin logo de plan: solo el icono de gráfica de líneas, sin
							    marco, en una caja del ancho del logo para que el texto quede alineado con los planes. */}
							<DropdownMenuItem
								onSelect={summary.onSelect}
								className="flex cursor-pointer items-center gap-2.5 py-2.5 hover:bg-gray-50 focus:bg-gray-50 focus:text-inherit dark:hover:bg-card dark:focus:bg-card dark:focus:text-inherit"
							>
								<span className="flex h-9 w-9 flex-none items-center justify-center">
									<ChartLine
										aria-hidden="true"
										className="h-5 w-5 text-gray-500 dark:text-muted-foreground"
									/>
								</span>
								<span className="flex-1 text-sm font-medium text-gray-900 dark:text-foreground">
									{summary.label}
								</span>
								{summary.active && (
									<Check
										className="h-5 w-5 flex-none text-arca-green dark:text-primary"
										data-testid="summary-selected-check"
									/>
								)}
							</DropdownMenuItem>
							<DropdownMenuSeparator />
						</>
					)}
					{options.map((option) => (
						<DropdownMenuItem
							key={option.id}
							onSelect={() => onSelect(option.id)}
							className="flex cursor-pointer items-center gap-2.5 py-2.5 hover:bg-gray-50 focus:bg-gray-50 focus:text-inherit dark:hover:bg-card dark:focus:bg-card dark:focus:text-inherit"
						>
							{option.logo('h-9 w-9')}
							<span className="min-w-0 flex-1">
								<span className="flex items-center gap-1.5">
									<span className="truncate text-sm font-medium text-gray-900 dark:text-foreground">
										{option.label}
									</span>
									{option.badge && (
										<Badge variant="outline" className="flex-none font-normal">
											{option.badge}
										</Badge>
									)}
								</span>
								{/* Bug real encontrado con axe, heredado de DesktopSidebarV2.tsx: text-gray-400
								    (#9ca3af) a 14px da 2.53:1 contra fondo blanco. gray-500 (#6b7280) da 4.83:1. */}
								{option.estado ? (
									<Badge variant="warning" className="mt-0.5 font-normal">
										{option.estado}
									</Badge>
								) : (
									option.subtitle && (
										<span className="block truncate text-sm text-gray-500 dark:text-muted-foreground">
											{option.subtitle}
										</span>
									)
								)}
							</span>
							{activa?.id === option.id && (
								<Check
									className="h-5 w-5 flex-none text-arca-green dark:text-primary"
									data-testid="plan-selected-check"
								/>
							)}
						</DropdownMenuItem>
					))}
					{addAction && (
						<>
							{/* Un botón secundario (outline), no una fila más: es una acción, y la de menos peso. */}
							<DropdownMenuItem asChild onSelect={addAction.onSelect}>
								<Button
									variant="outline"
									className="mt-1 w-full cursor-pointer justify-center gap-2 border-gray-200 text-gray-700 dark:border-border dark:text-foreground"
								>
									<Plus aria-hidden="true" className="h-4 w-4" />
									{addAction.label}
								</Button>
							</DropdownMenuItem>
						</>
					)}
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	);
};
